import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Tooltip,
  LinearProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlendPlayground from './BlendPlayground';
import VirtualizedExcelInput from '../../../utils/excelInput';

export default function AddItemDialog({
  open,
  onClose,
  // Quick-add state
  newItem,
  setNewItem,
  availableItems,
  availableShippingInstructions,
  onShippingInstructionChange,
  retrieveItems,
  retrieveShippingInstructions,
  onCreateItem,
  // Tab state
  addItemTab,
  setAddItemTab,
  // Playground
  currentItems,
  onPlaygroundSave,
  playgroundAvailableItems = [],
  versionSettings = null,
  // Excel state
  tableData,
  setTableData,
  excelImportRows,
  setExcelImportRows,
  validatingExcel,
  onRowSIChange,
  onValidateExcelRows,
  onCreateItemsFromExcel
}) {
  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>Add Items to Version</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={addItemTab} onChange={(e, newValue) => setAddItemTab(newValue)}>
            <Tab label="Quick Add" />
            <Tab label="Playground" />
          </Tabs>
        </Box>

        {addItemTab === 0 ? (
          <Stack spacing={2} sx={{ pt: 2 }}>
            <Autocomplete
              options={availableShippingInstructions}
              getOptionLabel={(option) =>
                `${option?.id || ''} - SI #${option?.finalShippingInstructionNumber || ''} - ${option?.shipmentRequestCustomerGradeMark || ''} - ${option?.destination || ''} - ${option?.shipmentRequestStorageQuantity || ''}`
              }
              value={newItem.shippingInstruction}
              onChange={(event, newValue) => onShippingInstructionChange(newValue)}
              onOpen={() => retrieveShippingInstructions(newItem.itemId)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Shipping Instruction"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
            {newItem.shippingInstruction && (
              <Box mt={1}>
                <Chip
                  label={`Selected SI: ${newItem.shippingInstruction.shippingInstructionNumber}`}
                  onDelete={() => {
                    setNewItem(prev => ({
                      ...prev,
                      shippingInstruction: null,
                      shippingInstructionId: null
                    }));
                    retrieveItems();
                  }}
                />
                <Typography variant="caption" display="block">
                  Destination: {newItem.shippingInstruction.destination}
                </Typography>
              </Box>
            )}
            <Autocomplete
              options={availableItems}
              getOptionLabel={(option) => {
                const customerGradeMark = newItem?.shippingInstruction?.shipmentRequestCustomerGradeMark || '';
                return `ID: ${option?.id || ''} - ${option?.buyingGrade || ''} - ${option?.origin || ''} - Crop: ${option?.crop || ''} ${customerGradeMark ? `- Grade: ${customerGradeMark}` : ''}`;
              }}
              value={newItem.item}
              onChange={(event, newValue) => {
                setNewItem(prev => ({
                  ...prev,
                  item: newValue,
                  itemId: newValue?.id
                }));
                if (newValue?.id) {
                  retrieveShippingInstructions(newValue.id);
                } else {
                  retrieveShippingInstructions();
                }
              }}
              onOpen={retrieveItems}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Item"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
            {newItem.item && (
              <Box mt={2}>
                <Chip
                  label={`Selected Item: ${newItem.item.id}`}
                  onDelete={() => {
                    setNewItem(prev => ({
                      ...prev,
                      item: null,
                      itemId: null
                    }));
                    retrieveShippingInstructions();
                  }}
                />
                <Typography variant="caption" display="block">
                  Description: {newItem.item.description}
                </Typography>
              </Box>
            )}
            <TextField
              label="Quantity"
              value={newItem.quantity}
              onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
            />
            <TextField
              label="Unit"
              value={newItem.unit}
              onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
            />
            <TextField
              label="Description"
              value={newItem.description}
              onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
            />
          </Stack>
        ) : (
          <Box sx={{ height: '70vh' }}>
            <BlendPlayground
              currentItems={currentItems}
              availableItems={playgroundAvailableItems}
              onSave={onPlaygroundSave}
              versionSettings={versionSettings}
            />
          </Box>
        )}

        {/* Excel Import Section */}
        <Box mt={4}>
          <Typography variant="subtitle1" gutterBottom>
            Paste Excel Below
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0,
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              width: 'fit-content'
            }}
          >
            {[
              { col: 'A', label: 'Customer Grade Mark', color: '#e3f2fd' },
              { col: 'B', label: 'Description (ignored)', color: '#f3e5f5' },
              { col: 'C', label: 'Quantity', color: '#fff3e0' },
              { col: 'D', label: 'Unit (optional)', color: '#e8f5e9' }
            ].map((col, idx) => (
              <Box
                key={col.col}
                sx={{
                  px: 2,
                  py: 0.75,
                  bgcolor: col.color,
                  borderRight: idx < 3 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  textAlign: 'center',
                  minWidth: 120
                }}
              >
                <Typography variant="caption" fontWeight="bold" display="block">
                  Column {col.col}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {col.label}
                </Typography>
              </Box>
            ))}
          </Box>
          <VirtualizedExcelInput
            tableData={tableData}
            setTableData={(data) => {
              setTableData(data);
              setExcelImportRows([]);
            }}
          />

          {/* Validation Results Table */}
          {excelImportRows.length > 0 && (
            <Box mt={2}>
              <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                <Typography variant="subtitle2">Validation Results</Typography>
                <Chip
                  size="small"
                  label={`${excelImportRows.filter(r => r.validated).length}/${excelImportRows.length} valid`}
                  color={excelImportRows.every(r => r.validated) ? 'success' : 'warning'}
                />
                {validatingExcel && <CircularProgress size={18} />}
              </Stack>
              {validatingExcel && <LinearProgress sx={{ mb: 1 }} />}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Customer Grade Mark</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>SI #</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Item ID</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Item Grade</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelImportRows.map((row, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: row.validated
                            ? 'rgba(76, 175, 80, 0.08)'
                            : row.error
                              ? 'rgba(244, 67, 54, 0.08)'
                              : 'inherit'
                        }}
                      >
                        <TableCell align="center">
                          {row.validating ? (
                            <CircularProgress size={18} />
                          ) : row.validated ? (
                            <Tooltip title="Validated">
                              <CheckCircleIcon color="success" fontSize="small" />
                            </Tooltip>
                          ) : row.error ? (
                            <Tooltip title={row.error}>
                              <ErrorIcon color="error" fontSize="small" />
                            </Tooltip>
                          ) : (
                            <HourglassEmptyIcon color="disabled" fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">{row.customerGradeMark}</TableCell>
                        <TableCell align="center">{row.quantity}</TableCell>
                        <TableCell align="center">{row.unit}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 280 }}>
                          {row.validating ? (
                            <CircularProgress size={16} />
                          ) : row.allMatchingSIs && row.allMatchingSIs.length > 1 ? (
                            <Select
                              size="small"
                              value={row.shippingInstruction?.id || ''}
                              onChange={(e) => {
                                const selected = row.allMatchingSIs.find(si => si.id === e.target.value);
                                if (selected) onRowSIChange(idx, selected);
                              }}
                              sx={{ fontSize: '0.75rem', minWidth: 260 }}
                            >
                              {row.allMatchingSIs.map(si => (
                                <MenuItem
                                  key={si.id}
                                  value={si.id}
                                  sx={si._sufficient === false ? { color: 'warning.main' } : {}}
                                >
                                  {si._sufficient === false ? '⚠ ' : si._sufficient === 'unknown' ? '? ' : ''}
                                  {`${si?.id || ''} - SI #${si.finalShippingInstructionNumber || si.id} | Storage: ${si.shipmentRequestStorageQuantity ?? 'N/A'}`}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : row.shippingInstruction ? (
                            `${row.shippingInstruction.id} | SI #${row.shippingInstruction.finalShippingInstructionNumber || row.shippingInstruction.id} | Storage: ${row.shippingInstruction.shipmentRequestStorageQuantity ?? 'N/A'}`
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {row.item ? row.item.id : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {row.item ? (row.item.buyingGrade || row.item.origin || '') : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {row.error ? (
                            <Typography variant="caption" color="error">
                              {row.error}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {addItemTab === 0 && (
          <>
            <Button
              onClick={onCreateItem}
              variant="contained"
              disabled={!newItem.item || !newItem.item.id || !newItem.quantity}
            >
              Add Item
            </Button>
            <Button
              onClick={onValidateExcelRows}
              variant="outlined"
              color="info"
              disabled={!tableData || tableData.length === 0 || validatingExcel}
              startIcon={validatingExcel ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              {validatingExcel ? 'Validating...' : 'Validate Excel'}
            </Button>
            <Button
              onClick={onCreateItemsFromExcel}
              variant="contained"
              color="success"
              disabled={!excelImportRows.some(r => r.validated) || validatingExcel}
            >
              Add From Excel ({excelImportRows.filter(r => r.validated).length})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
